import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TfService, LoginRequest, AuthInfo } from '../tf-service.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import { LoginModel } from '../model/tf-login';
import {
  Domains,
  MustMatch,
  passwordExcludesEmailLocalPartValidator,
  passwordNotRepeatedCharacterValidator,
} from 'src/app/validation-default/domains';
import { BasketService } from 'src/app/customer/cp-customer/cp-shopping-basket/service/shopping-basket.service';
import { NotificationService } from 'src/app/Theme/layout/admin/notification.service';
import { CustomToastService } from 'src/app/Theme/shared/service/toast.service';
import { FeatherModule } from 'angular-feather';
import { CpCompanyService } from 'src/app/admin/tf-admin/tf-configuration/tf-company/service/cp-company.service';
import { DataFormatService } from 'src/app/Theme/shared/service/data-format.service';
import { WrongComponent } from 'src/app/Theme/shared/components/wrong/wrong.component';
import { ServerUnavailableComponent } from 'src/app/Theme/shared/components/server-unavailable/server-unavailable.component';
import { PleaseWaitComponent } from 'src/app/Theme/shared/components/please-wait/please-wait.component';
import { SpinnerService } from 'src/app/Theme/shared/service/spinner.service';
import { SecurityConfigService } from 'src/app/Theme/shared/service/security-config/security-config.service';
import { AuthMethod } from 'src/app/Theme/shared/service/security-config/security-config.model';
import QRCode from 'qrcode';

type LoginStep = 'credentials' | 'mfa' | 'new_password' | 'email_otp' | 'mfa_setup';

interface AuthChallengeState {
  challenge: string;
  session: string;
  email: string;
  message?: string;
  secretCode?: string;
  otpauthUrl?: string;
}

/** Matches the backend per-email cooldown between email-code requests. */
const OTP_RESEND_COOLDOWN_SECONDS = 60;

@Component({
  selector: 'app-tf-login',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    FeatherModule,
    WrongComponent,
    ServerUnavailableComponent,
    PleaseWaitComponent,
  ],
  templateUrl: './tf-login.component.html',
  styleUrls: ['./tf-login.component.scss']
})
export class TfLoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  challengeForm: FormGroup;
  loginModel: LoginModel;
  submitted = false;
  msgError = '';
  dismissible = true;
  user: any;
  userId;
  backendError = '';
  logoImageUrl: string;
  companyName: string;
  copyrightText: string = '';
  currentYear: number = new Date().getFullYear();
  readonly zumConnectPortalUrl = 'https://p2-innovate.com/offering/zum-connect-customer-portal/';
  serverUnavailable = false;
  foundError = false;
  isProcessing = false;
  showPleaseWait = false;
  showPassword = false;
  showChallengePassword = false;
  showChallengeConfirmPassword = false;
  loginStep: LoginStep = 'credentials';
  authChallenge: AuthChallengeState | null = null;
  authMethod: AuthMethod = 'PASSWORD';
  /** Informational banner (sign-in settings changed, code resent). */
  noticeMessage = '';
  resendCountdown = 0;
  mfaSetupSecret = '';
  mfaSetupQrDataUrl = '';
  mfaSetupLoading = false;
  mfaSecretCopied = false;
  private resendTimer: ReturnType<typeof setInterval> | null = null;

  /** Full Please Wait screen only after this latency (avoid flashing on normal login). */
  private readonly pleaseWaitDelayMs = 5000;
  private pleaseWaitTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private tfService: TfService,
    private formBuilder: FormBuilder,
    private router: Router,
    public translate: TranslateService,
    private basketService: BasketService,
    private NotificationService: NotificationService,
    private toastService: CustomToastService,
    public CpCompanyService: CpCompanyService,
    private dataFormatService: DataFormatService,
    private spinnerService: SpinnerService,
    private securityConfigService: SecurityConfigService,
    private route: ActivatedRoute
  ) {}

  get isPasswordless(): boolean {
    return this.authMethod === 'EMAIL_OTP';
  }

  ngOnInit(): void {
    this.CpCompanyService.pictureUrlLogo$.subscribe(url => {
      if (url) {
        this.logoImageUrl = url;
      }
    });

    this.updateCopyrightText();

    if (!this.CpCompanyService['pictureUrlSourceLogo'].getValue()) {
      this.CpCompanyService.getCompany().subscribe(
        (response: any) => {
          const logoUrl = response.logo_url || 'assets/images/logo.png';
          this.logoImageUrl = logoUrl;
          this.CpCompanyService.updateLogoUrl(logoUrl);
          const favIconUrl = response.favIcon;
          if (favIconUrl) {
            this.CpCompanyService.updateFavIconUrl(favIconUrl);
          }
        },
        (error) => console.log(error)
      );
    }
    this.getValidation();

    if (this.route.snapshot.queryParamMap.get('reason') === 'auth_config_changed') {
      this.noticeMessage = this.translate.instant('login.AuthConfigChanged');
    }
    this.loadAuthMethod();
  }

  ngOnDestroy(): void {
    this.clearPleaseWaitTimer();
    this.clearResendTimer();
  }

  getValidation(): void {
    this.loginModel = new LoginModel('', '');
    this.loginForm = this.formBuilder.group({
      email: [this.loginModel.email, Domains.getEmailValidators()],
      password: [this.loginModel.password, Domains.getRequiredAndMaxLength50Validators()],
    });

    this.challengeForm = this.formBuilder.group({
      mfaCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      newPassword: ['', [
        ...Domains.getPasswordValidators(),
        passwordNotRepeatedCharacterValidator(),
        passwordExcludesEmailLocalPartValidator(() => this.loginEmailForPassword()),
      ]],
      confirmPassword: ['', Validators.required],
      // Cognito email codes are 8 digits; accept 6 in case the pool is configured shorter.
      otpCode: ['', [Validators.required, Validators.pattern(/^\d{6,8}$/)]],
      deviceName: [''],
    }, {
      validators: MustMatch('newPassword', 'confirmPassword'),
    });
  }

  private loginEmailForPassword(): string {
    return this.authChallenge?.email || this.loginForm?.get('email')?.value || '';
  }

  showNewPasswordError(): boolean {
    const control = this.challengeForm.get('newPassword');
    return !!control && control.invalid && (control.dirty || control.touched || this.submitted);
  }

  showConfirmPasswordError(): boolean {
    const control = this.challengeForm.get('confirmPassword');
    return !!control && control.invalid && (control.dirty || control.touched || this.submitted);
  }

  /** Falls back to PASSWORD; the backend answers AUTH_METHOD_CHANGED if that is wrong. */
  private loadAuthMethod(): void {
    this.securityConfigService.getPublicConfig().subscribe({
      next: (config) => this.applyAuthMethod(config?.authMethod),
      error: () => this.applyAuthMethod('PASSWORD'),
    });
  }

  private applyAuthMethod(method: AuthMethod | string | undefined): void {
    this.authMethod = method === 'EMAIL_OTP' ? 'EMAIL_OTP' : 'PASSWORD';
    const password = this.loginForm.get('password');
    if (this.isPasswordless) {
      password?.reset();
      password?.disable();
    } else {
      password?.enable();
    }
  }

  get f() {
    return this.loginForm.controls;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleChallengePasswordVisibility(): void {
    this.showChallengePassword = !this.showChallengePassword;
  }

  toggleChallengeConfirmPasswordVisibility(): void {
    this.showChallengeConfirmPassword = !this.showChallengeConfirmPassword;
  }

  resetLoginFlow(): void {
    this.loginStep = 'credentials';
    this.authChallenge = null;
    this.challengeForm.reset();
    this.backendError = '';
    this.submitted = false;
    this.showChallengePassword = false;
    this.showChallengeConfirmPassword = false;
    this.resetMfaSetup();
    this.clearResendTimer();
  }

  login(loginForm: { email: string; password?: string }): void {
    this.submitted = true;
    this.backendError = '';
    this.noticeMessage = '';
    this.serverUnavailable = false;
    this.foundError = false;

    if (this.loginForm.invalid) {
      return;
    }

    if (this.isPasswordless) {
      this.performLogin({ email: loginForm.email });
      return;
    }
    this.performLogin({ email: loginForm.email, password: loginForm.password });
  }

  resendEmailCode(): void {
    if (!this.authChallenge || this.resendCountdown > 0 || this.isProcessing) {
      return;
    }
    this.backendError = '';
    this.performLogin({ email: this.authChallenge.email }, () => {
      this.noticeMessage = this.translate.instant('login.EmailCodeResent');
    });
  }

  async copyMfaSetupSecret(): Promise<void> {
    if (!this.mfaSetupSecret) {
      return;
    }
    try {
      await navigator.clipboard.writeText(this.mfaSetupSecret);
      this.mfaSecretCopied = true;
      setTimeout(() => {
        this.mfaSecretCopied = false;
      }, 2000);
    } catch {
      this.toastService.showError(
        this.translate.instant('TFMfa.copyFailed'),
        this.translate.instant('Error'),
      );
    }
  }

  onDigitsInput(controlName: string, value: string, maxLength: number): void {
    const digits = (value || '').replace(/\D/g, '').slice(0, maxLength);
    this.challengeForm.get(controlName)?.setValue(digits, { emitEvent: false });
  }

  submitChallenge(): void {
    this.submitted = true;
    this.backendError = '';
    this.serverUnavailable = false;
    this.foundError = false;

    if (!this.authChallenge) {
      return;
    }

    if (this.loginStep === 'mfa') {
      const mfaCode = this.challengeForm.get('mfaCode')?.value;
      if (!mfaCode || this.challengeForm.get('mfaCode')?.invalid) {
        return;
      }
      this.performLogin({
        email: this.authChallenge.email,
        session: this.authChallenge.session,
        challenge: this.authChallenge.challenge,
        mfa_code: mfaCode,
      });
      return;
    }

    if (this.loginStep === 'email_otp') {
      const otpControl = this.challengeForm.get('otpCode');
      if (!otpControl?.value || otpControl.invalid) {
        return;
      }
      this.noticeMessage = '';
      this.performLogin({
        email: this.authChallenge.email,
        session: this.authChallenge.session,
        challenge: 'EMAIL_OTP',
        code: otpControl.value,
      });
      return;
    }

    if (this.loginStep === 'mfa_setup') {
      const codeControl = this.challengeForm.get('mfaCode');
      if (!this.mfaSetupSecret || !codeControl?.value || codeControl.invalid) {
        return;
      }
      const deviceName = (this.challengeForm.get('deviceName')?.value as string)?.trim();
      this.performLogin({
        email: this.authChallenge.email,
        session: this.authChallenge.session,
        challenge: 'MFA_SETUP',
        code: codeControl.value,
        ...(deviceName ? { deviceName } : {}),
      });
      return;
    }

    if (this.loginStep === 'new_password') {
      const newPasswordControl = this.challengeForm.get('newPassword');
      const confirmPasswordControl = this.challengeForm.get('confirmPassword');
      newPasswordControl?.markAsTouched();
      confirmPasswordControl?.markAsTouched();
      this.challengeForm.updateValueAndValidity();
      if (newPasswordControl?.invalid || confirmPasswordControl?.invalid) {
        return;
      }
      this.performLogin({
        email: this.authChallenge.email,
        session: this.authChallenge.session,
        challenge: this.authChallenge.challenge,
        new_password: newPasswordControl?.value,
      });
    }
  }

  private performLogin(payload: LoginRequest, onChallenge?: () => void): void {
    this.startProcessing();

    this.tfService.signIn(payload).pipe(
      finalize(() => this.stopProcessing())
    ).subscribe({
      next: (authInfo) => {
        this.handleLoginSuccess(authInfo);
        if (authInfo.challenge) {
          onChallenge?.();
        }
      },
      error: (error: HttpErrorResponse) => this.handleLoginError(error),
    });
  }

  private handleLoginSuccess(authInfo: AuthInfo): void {
    if (!authInfo.isAuthenticated && authInfo.challenge && authInfo.session) {
      this.applyAuthChallenge({
        challenge: authInfo.challenge,
        session: authInfo.session,
        email: authInfo.email || this.loginForm.get('email')?.value || '',
        secretCode: authInfo.secretCode,
        otpauthUrl: authInfo.otpauthUrl,
      });
      return;
    }

    this.resetLoginFlow();
    console.log('User authenticated:', authInfo);

    if (authInfo.userId) {
      const user = {
        userId: authInfo.userId,
        username: authInfo.username,
      };
      this.tfService.addUserToLocalCache(user as any);
      this.dataFormatService.ensureProfileFormatLoaded().subscribe();
    }

    if (authInfo.userGroups) {
      localStorage.setItem('userGroup', JSON.stringify(authInfo.userGroups));
    }

    if (!authInfo.isAuthenticated) {
      console.log('Authentication response did not indicate successful login.');
      return;
    }

    console.log('User is authenticated, redirecting...');
    // Notifications, portal resolve-batch, and session flags run in checkAuthStatus (called from signIn).
    this.navigateAfterLogin(authInfo.userGroups);
  }

  private handleLoginError(error: HttpErrorResponse): void {
    console.error('Login failed:', error);
    const body = error.error ?? {};
    this.mfaSetupLoading = false;

    // Backward compat if an environment still returns challenge on 401
    if (body.challenge && body.session) {
      this.applyAuthChallenge({
        challenge: body.challenge,
        session: body.session,
        email: body.email || this.loginForm.get('email')?.value || '',
        message: body.message,
      });
      return;
    }

    if (this.isServerUnavailableError(error)) {
      this.serverUnavailable = true;
      this.foundError = false;
      this.backendError = '';
      return;
    }

    const code = typeof body.code === 'string' ? body.code : '';

    if (code === 'AUTH_METHOD_CHANGED') {
      this.resetLoginFlow();
      this.applyAuthMethod(body.authMethod);
      this.foundError = false;
      this.noticeMessage = this.translate.instant('login.AuthMethodChanged');
      return;
    }

    if (code === 'SESSION_EXPIRED' && this.loginStep !== 'credentials') {
      this.resetLoginFlow();
      this.foundError = false;
      this.backendError = this.translate.instant('login.SessionExpired');
      return;
    }

    // Shown inline: the generic error screen would hide the form the user must retry from.
    if (code === 'TOO_MANY_ATTEMPTS' || error.status === 429) {
      this.foundError = false;
      this.backendError = this.translate.instant('login.TooManyAttempts');
      return;
    }

    const errorMessage = this.resolveLoginErrorMessage(body);

    if (this.loginStep !== 'credentials') {
      this.foundError = false;
      this.backendError = errorMessage;
      return;
    }

    // Rejected credentials are a normal outcome: keep the form and notify via toast.
    if (this.isInvalidCredentialsError(error, code)) {
      this.foundError = false;
      this.backendError = '';
      this.loginForm.get('password')?.reset();
      this.toastService.showError(
        this.translate.instant('login.InvalidCredentials'),
        this.translate.instant('Error'),
      );
      return;
    }

    this.foundError = true;
    this.backendError = errorMessage;
  }

  private isInvalidCredentialsError(error: HttpErrorResponse, code: string): boolean {
    // Older backends answer a bare 401 without a code.
    return code === 'INVALID_CREDENTIALS' || (!code && error.status === 401);
  }

  private applyAuthChallenge(state: AuthChallengeState): void {
    this.authChallenge = {
      challenge: state.challenge,
      session: state.session,
      email: state.email,
      message: state.message,
    };
    this.submitted = false;
    this.backendError = '';
    this.foundError = false;
    this.serverUnavailable = false;

    if (state.challenge === 'MFA_SETUP') {
      // First MFA_SETUP response has no secret yet; the follow-up call returns it.
      if (this.loginStep !== 'mfa_setup') {
        this.challengeForm.reset();
        this.resetMfaSetup();
      }
      this.loginStep = 'mfa_setup';
      if (state.secretCode) {
        this.showMfaSetupSecret(state.secretCode, state.otpauthUrl);
      } else {
        this.mfaSetupLoading = true;
        // Deferred so the current request's finalize() runs before the next one starts.
        setTimeout(() => this.startMfaSetup());
      }
      return;
    }

    this.challengeForm.reset();

    if (state.challenge === 'NEW_PASSWORD_REQUIRED') {
      this.loginStep = 'new_password';
    } else if (state.challenge === 'SOFTWARE_TOKEN_MFA' || state.challenge === 'SMS_MFA') {
      this.loginStep = 'mfa';
    } else if (state.challenge === 'EMAIL_OTP') {
      this.loginStep = 'email_otp';
      this.startResendCountdown();
    } else {
      this.backendError = state.message || this.translate.instant('login.LoginFailed');
    }
  }

  private startMfaSetup(): void {
    if (!this.authChallenge) {
      return;
    }
    this.performLogin({
      email: this.authChallenge.email,
      session: this.authChallenge.session,
      challenge: 'MFA_SETUP',
    });
  }

  private async showMfaSetupSecret(secretCode: string, otpauthUrl?: string): Promise<void> {
    this.mfaSetupSecret = secretCode;
    try {
      this.mfaSetupQrDataUrl = otpauthUrl
        ? await QRCode.toDataURL(otpauthUrl, { width: 220, margin: 1 })
        : '';
    } catch {
      this.mfaSetupQrDataUrl = '';
    }
    this.mfaSetupLoading = false;
  }

  private resetMfaSetup(): void {
    this.mfaSetupSecret = '';
    this.mfaSetupQrDataUrl = '';
    this.mfaSetupLoading = false;
    this.mfaSecretCopied = false;
  }

  private startResendCountdown(): void {
    this.clearResendTimer();
    this.resendCountdown = OTP_RESEND_COOLDOWN_SECONDS;
    this.resendTimer = setInterval(() => {
      this.resendCountdown -= 1;
      if (this.resendCountdown <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimer !== null) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
    this.resendCountdown = 0;
  }

  private resolveLoginErrorMessage(body: Record<string, unknown>): string {
    const code = typeof body['code'] === 'string' ? body['code'] : '';
    const codeMessages: Record<string, string> = {
      INVALID_CREDENTIALS: this.translate.instant('login.LoginFailed'),
      INVALID_MFA_CODE: this.translate.instant('login.InvalidMfaCode'),
      INVALID_OTP_CODE: this.translate.instant('login.InvalidOtpCode'),
      SESSION_EXPIRED: this.translate.instant('login.SessionExpired'),
      NEW_PASSWORD_INVALID: this.translate.instant('login.PasswordIsValid'),
      TOO_MANY_ATTEMPTS: this.translate.instant('login.TooManyAttempts'),
    };

    if (code && codeMessages[code]) {
      return codeMessages[code];
    }

    const errorText = typeof body['error'] === 'string' ? body['error'] : '';
    const messageText = typeof body['message'] === 'string' ? body['message'] : '';
    return errorText || messageText || this.translate.instant('login.LoginFailed');
  }

  get challengeTitleKey(): string {
    if (this.loginStep === 'mfa') {
      return this.authChallenge?.challenge === 'SMS_MFA'
        ? 'login.SmsMfaTitle'
        : 'login.MfaTitle';
    }
    if (this.loginStep === 'new_password') {
      return 'login.NewPasswordRequiredTitle';
    }
    if (this.loginStep === 'email_otp') {
      return 'login.EmailCodeTitle';
    }
    if (this.loginStep === 'mfa_setup') {
      return 'login.MfaSetupTitle';
    }
    return '';
  }

  private isServerUnavailableError(error: any): boolean {
    const httpError = error instanceof HttpErrorResponse
      ? error
      : (error?.error instanceof HttpErrorResponse ? error.error : error);

    const status = httpError?.status ?? error?.status;
    if (status === 0 || status === 502 || status === 503 || status === 504) {
      return true;
    }
    if (typeof status === 'number' && status >= 500) {
      return true;
    }

    const networkPayload = httpError?.error ?? error?.error;
    if (typeof ProgressEvent !== 'undefined' && networkPayload instanceof ProgressEvent) {
      return true;
    }

    const message = String(httpError?.message || error?.message || '').toLowerCase();
    return (
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('network error') ||
      message.includes('err_internet_disconnected') ||
      message.includes('err_connection') ||
      message.includes('err_name_not_resolved') ||
      message.includes('err_connection_refused')
    );
  }

  retryLogin(): void {
    this.serverUnavailable = false;
    this.foundError = false;
    this.backendError = '';

    if (this.loginStep === 'credentials' && this.loginForm?.valid) {
      this.login(this.loginForm.value);
      return;
    }

    if (this.loginStep !== 'credentials') {
      this.submitChallenge();
    }
  }

  dismissServerUnavailable(): void {
    this.serverUnavailable = false;
    this.backendError = '';
  }

  tryAgain(): void {
    this.foundError = false;
    this.backendError = '';
    this.submitted = false;
  }

  dismissWrong(): void {
    this.foundError = false;
    this.backendError = '';
    this.resetLoginFlow();
  }

  private startProcessing(): void {
    this.isProcessing = true;
    this.showPleaseWait = false;
    this.clearPleaseWaitTimer();
    this.spinnerService.forceHide();

    this.pleaseWaitTimer = setTimeout(() => {
      if (!this.isProcessing) {
        return;
      }
      this.showPleaseWait = true;
    }, this.pleaseWaitDelayMs);
  }

  private stopProcessing(): void {
    this.clearPleaseWaitTimer();
    this.isProcessing = false;
    this.showPleaseWait = false;
  }

  private clearPleaseWaitTimer(): void {
    if (this.pleaseWaitTimer !== null) {
      clearTimeout(this.pleaseWaitTimer);
      this.pleaseWaitTimer = null;
    }
  }

  private navigateAfterLogin(userGroups: string[] | undefined): void {
    const url = userGroups?.includes('admin')
      ? '/admin/cf-dashboard'
      : this.tfService.getDefaultCustomerDashboardUrlForGroups(userGroups);

    if (!userGroups?.includes('admin')) {
      this.basketService.getBasketsList().subscribe({
        next: (baskets) => {
          console.log('getBasketsList subscribe fired, data:', baskets);
        },
        error: (err) => {
          console.error('Error fetching baskets:', err);
        }
      });
    }

    this.router.navigateByUrl(url);
  }

  updateCopyrightText(): void {
    const company = this.translate.instant('auth.Brand');
    const copyrightTemplate = this.translate.instant('auth.Copyright');
    if (copyrightTemplate && copyrightTemplate !== 'auth.Copyright') {
      this.copyrightText = copyrightTemplate
        .replace('{{year}}', this.currentYear.toString())
        .replace('{{companyName}}', company);
    } else {
      this.copyrightText = `© ${this.currentYear} ${company}. All rights reserved.`;
    }
  }
}
